import { Component } from '@theme/component';
import { fetchConfig, preloadImage, prefersReducedMotion } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * Where the PDP's buy-buttons row stops being the button beside the variant picker and
 * becomes the bar pinned to the bottom of the screen. Kept in step with the
 * `position: fixed` block in sections/product-information.liquid, which is what pins it.
 */
const PINNED_BUY_BAR = matchMedia('(max-width: 767px)');

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
    PINNED_BUY_BAR.addEventListener('change', this.syncAwaitingSizeState);
    this.syncAwaitingSizeState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
    PINNED_BUY_BAR.removeEventListener('change', this.syncAwaitingSizeState);
  }

  /**
   * Whether this CTA is the one pinned to the bottom of the screen rather than the one
   * sitting beside the variant picker.
   *
   * The same three conditions the CSS that pins it uses: the phone breakpoint, the product
   * template, and the details column - which is what keeps the quick-add drawer out, since
   * assets/quick-add.js drops `.product-details` from it below 768px.
   *
   * @returns {boolean}
   */
  get #isPinnedBuyBar() {
    return (
      PINNED_BUY_BAR.matches &&
      this.closest('main[data-template^="product"] .product-details .buy-buttons-block') != null
    );
  }

  /**
   * Decides whether a CTA that is still asking for a size can be clicked.
   *
   * Dead is the default, and what Liquid renders: beside the picker there is nothing for
   * the click to do that the picker itself does not, and the desktop floating bar is where
   * the jump is offered from (assets/sticky-add-to-cart.js). Pinned to the bottom of a
   * phone the same button is the only thing on screen, with the picker scrolled far above
   * it, so there it comes alive and `handleClick` sends the click to the Size field.
   *
   * Only ever touches a CTA in that state; one dead for any other reason - sold out, a
   * market that cannot sell - is left exactly as rendered. Re-run on a resize past the
   * breakpoint, and by `onProductSelect` after a render morphs the button.
   */
  syncAwaitingSizeState = () => {
    if (this.dataset.awaitingSize !== 'true') return;

    const button = this.refs.addToCartButton;

    if (button instanceof HTMLButtonElement) button.disabled = !this.#isPinnedBuyBar;
  };

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    // A link CTA never navigates: intercept the default up front so every early
    // return below (disabled, validity, quantity cap) means "do nothing" instead
    // of falling through to the PDP.
    const isAnchorCta = this.refs.addToCartButton instanceof HTMLAnchorElement;
    if (isAnchorCta) event.preventDefault();

    // Anchor CTAs can't be natively disabled: honour aria-disabled by doing
    // nothing, like the <button disabled> variant blocking clicks entirely.
    if (this.refs.addToCartButton?.getAttribute('aria-disabled') === 'true') return;

    // A CTA still naming the size it is waiting for is a way back to that field, not an
    // add. Only reachable where `syncAwaitingSizeState` has brought this button to life:
    // pinned to the bottom of a phone, with the picker scrolled far above it. Beside the
    // picker on desktop the button stays dead and the same jump is offered by the floating
    // bar instead - see `handleAddToCartClick` in assets/sticky-add-to-cart.js.
    if (this.dataset.awaitingSize === 'true') {
      this.#scrollToSizePicker();
      return;
    }

    // The back-in-stock CTA is live on purpose - it is a drawer opener, not an add - so it
    // must not run the add animation, and `handleSubmit` drops the submit that follows.
    if (this.dataset.notify === 'true') {
      this.#openNotifyDrawer();
      return;
    }

    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    // When the CTA is a link (it carries the PDP href so crawlers can follow a
    // real link), the click doesn't submit the form natively: submit
    // programmatically so the add happens instead of navigating to the PDP.
    if (isAnchorCta) {
      form.requestSubmit();
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  /**
   * The picker this CTA belongs to. Scoped to the section - or to the dialog, for the
   * quick-add drawer, which renders a second copy of the whole details column - so the
   * tag alone can't hand back someone else's picker.
   *
   * @returns {HTMLElement | null}
   */
  get #variantPicker() {
    const productId = /** @type {HTMLElement | null} */ (this.closest('product-form-component'))?.dataset.productId;
    if (!productId) return null;

    const scope = this.closest('.shopify-section, dialog') ?? document;
    const picker = scope.querySelector(`variant-picker[data-product-id="${productId}"]`);

    return picker instanceof HTMLElement ? picker : null;
  }

  /**
   * Brings the Size field back on screen and puts the keyboard on the first size the
   * shopper can pick.
   *
   * The Size fieldset rather than the picker as a whole - Colour sits above it and a CTA
   * saying "Select a size" should land on the sizes - falling back to the picker for a
   * product whose Size option isn't drawn as a grid.
   *
   * `scrollIntoView` and no offset arithmetic of its own, for the reasons
   * assets/sticky-add-to-cart.js gives for the same jump: `block: 'center'` clears the
   * header above and the pinned buy bar below at every width.
   */
  #scrollToSizePicker() {
    const picker = this.#variantPicker;
    if (!picker) return;

    const target = picker.querySelector('.variant-option--sizes') ?? picker;

    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'center',
    });

    // Focus follows the jump, so a keyboard or screen reader user carries on from the
    // field the CTA named rather than from a bar that is now behind them. `disabled` is
    // worn only by sizes the selected fit doesn't run - a sold-out one is still a size to
    // pick, since picking it turns the CTA into the back-in-stock notice. The scroll above
    // has already placed the field, hence `preventScroll`.
    const firstSize = target.querySelector('input:not([disabled])');

    if (firstSize instanceof HTMLElement) firstSize.focus({ preventScroll: true });
  }

  /**
   * Opens the back-in-stock drawer this CTA stands in for
   * (snippets/notify-me-drawer.liquid), pointed at the variant the shopper picked.
   *
   * The variant id is read from `data-notify-variant` before the form's own
   * input: a sold-out size turns the CTA into this notice on the click itself
   * (see `previewSizeSelection`), while `refs.variantId` only catches up when
   * the section render lands - so between the two, the form's input still holds
   * the size the shopper moved away from.
   */
  #openNotifyDrawer() {
    const drawerId = this.dataset.notifyDrawer;
    if (!drawerId) return;

    // Not `getElementById`: the drawer is a custom element, and asking for the
    // tag as well means a stale id can't hand us something that isn't one.
    const drawer = document.querySelector(`theme-drawer#${CSS.escape(drawerId)}`);
    if (!(drawer instanceof HTMLElement) || typeof (/** @type {any} */ (drawer).open) !== 'function') return;

    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const variantId = this.dataset.notifyVariant || productForm?.refs.variantId?.value;

    /** @type {any} */ (drawer.querySelector('notify-me-form-component'))?.prepare?.(variantId);
    /** @type {any} */ (drawer).open();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Clears any pending state-reset timeouts.
   */
  #clearResetTimeouts() {
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];
  }

  /**
   * Animates the add to cart button through its loading and success states,
   * then returns it to its default state. Purely a visual sequence — it
   * isn't tied to the actual add-to-cart request's timing or outcome.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    this.#clearResetTimeouts();
    const resetTimeouts = /** @type {number[]} */ (this.#resetTimeouts);

    addToCartButton.dataset.state = 'loading';

    const successTimeoutId = setTimeout(() => {
      addToCartButton.dataset.state = 'success';

      const resetTimeoutId = setTimeout(() => {
        delete addToCartButton.dataset.state;

        const index = resetTimeouts.indexOf(resetTimeoutId);
        if (index > -1) {
          resetTimeouts.splice(index, 1);
        }
      }, 600);

      resetTimeouts.push(resetTimeoutId);

      const index = resetTimeouts.indexOf(successTimeoutId);
      if (index > -1) {
        resetTimeouts.splice(index, 1);
      }
    }, 2000);

    resetTimeouts.push(successTimeoutId);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString(),
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    // Nothing to add: the CTA is standing in for the back-in-stock notice - see
    // `data-notify` in snippets/add-to-cart-button.liquid.
    if (this.refs.addToCartButtonContainer?.dataset.notify === 'true') return;

    // Nothing to add either while the CTA is still asking for a size. As the pinned mobile
    // buy bar that button is live so it can be tapped, but the variant behind it is
    // Shopify's pick rather than the shopper's - so an Enter on it must not add a size
    // nobody chose. `handleClick` sends the click itself to the picker.
    if (this.refs.addToCartButtonContainer?.dataset.awaitingSize === 'true') return;

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled,
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: /** @type {string} */ (formData.get('id')),
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
      }),
    );

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            }),
          );

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                },
              }),
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#refreshCart()
            .then((ajaxCart) => {
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                  sections: response.sections,
                  didError: false,
                },
              });

              if (this.#getVariantIdInput()) {
                this.#updateCartQuantity(ajaxCart);
              }

              return ajaxCart;
            })
            .catch(deferredEventPromise.reject);
        }
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          }),
        );
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      }),
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            }),
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount: totalQuantity,
                  productId: this.dataset.productId,
                },
              }),
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(response.message));
          }
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
        this.#updateCartQuantity(cart);
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          }),
        );
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * Answers a size click on the click, instead of a section render later.
   *
   * Almost everything the CTA shows needs the server - the price, how many are left,
   * whether a quantity rule has been hit. Whether *this* variant can be added does not:
   * the picker renders `data-option-available` on every option value, so the answer is
   * already in the DOM the moment the shopper picks a size. Painting it here is the same
   * bargain variant-picker.js strikes for the option labels - a preview of the render
   * that is already on its way, which the morph then replaces with the server's own.
   *
   * Only the Size option. A colour value's `available` means "some size in this colour is
   * buyable", which says nothing about the CTA, and on this theme a colour pick clears the
   * size anyway (snippets/variant-main-picker.liquid) - so its CTA is the response's to
   * draw.
   * @param {ProductSelectEvent} event
   */
  #previewSizeSelection(event) {
    const picker = event.target;
    const optionValueId = event.detail?.optionValueId;
    if (!(picker instanceof HTMLElement) || !optionValueId) return;

    const sizeOptionName = picker.dataset.sizeOptionName;
    const picked = picker.querySelector(`[data-option-value-id="${optionValueId}"]`);
    if (!sizeOptionName || !(picked instanceof HTMLElement)) return;
    if (picked.dataset.optionName !== sizeOptionName) return;

    // No variant behind the value - a size the selected fit doesn't run - says nothing
    // about what can be added, so that one waits for the response like everything else.
    if (!picked.dataset.variantId) return;

    const { addToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    if (!addToCartButtonContainer) return;

    const available = picked.dataset.optionAvailable !== 'false';

    // A sold-out size the shopper picked is still a live CTA - it becomes the back-in-stock
    // notice rather than a dead button (Figma node 6350:73236) - so the button is enabled
    // either way here. Only the buying is off: nothing can be added, and the accelerated
    // checkout goes with it.
    addToCartButtonContainer.enable();

    // And the wait for a size is over, on the click rather than on the render that follows
    // it: the flag is what makes this CTA a jump back to the picker instead of an add
    // (`handleClick`), and leaving it on would send the shopper back to the field they have
    // just answered. Put back by `onProductSelect` if the response says the size is gone
    // again - a colour click clears it (snippets/variant-main-picker.liquid).
    addToCartButtonContainer.removeAttribute('data-awaiting-size');

    acceleratedCheckoutButtonContainer?.toggleAttribute('hidden', !available);

    // `data-notify` is written by value, not toggled: `toggleAttribute` sets an
    // empty string, and every reader of this state tests `dataset.notify === 'true'`
    // against the value Liquid renders. Toggled, the CSS still painted the notice
    // (it selects on the attribute alone) while `handleClick` and `handleSubmit`
    // both read it as not-the-notice and added to cart instead - which is what a
    // shopper got anywhere the state can only come from a size click, the
    // quick-add drawer being the case where it always does (it opens with no size
    // chosen, so Liquid never renders the notice itself).
    //
    // The variant the notice stands for goes with it, so the two can never
    // disagree; the CTA reads it when it opens the drawer, ahead of
    // `refs.variantId` catching up - see `openNotifyDrawer`.
    //
    // So does `aria-haspopup`: the CTA stops being a submit and becomes a drawer
    // opener, and a screen reader announcing "Notify me" alone gives no hint that
    // a dialog is about to take focus. By value again - an empty `aria-haspopup`
    // reads as "false", not as a dialog.
    const notifyButton = addToCartButtonContainer.refs.addToCartButton;
    if (available) {
      addToCartButtonContainer.removeAttribute('data-notify');
      delete addToCartButtonContainer.dataset.notifyVariant;
      notifyButton?.removeAttribute('aria-haspopup');
    } else {
      addToCartButtonContainer.setAttribute('data-notify', 'true');
      addToCartButtonContainer.dataset.notifyVariant = picked.dataset.variantId;
      notifyButton?.setAttribute('aria-haspopup', 'dialog');
    }

    const label = addToCartButtonContainer.querySelector('.add-to-cart-text__content > span > span');
    const text = available
      ? addToCartButtonContainer.dataset.textAvailable
      : addToCartButtonContainer.dataset.textNotify;
    if (label && text) label.textContent = text;
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;

    this.#previewSizeSelection(event);

    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state. A sold-out variant normally means a dead button, but not
      // when the CTA has become the back-in-stock notice: that one is live by design, and
      // disabling it here is what made it go dead a second after the click.
      const notify = currentAddToCartButtonContainer.dataset.textNotify != null;

      // Whether the page is waiting on a size again is the response's to say, and only the
      // response's: `previewSizeSelection` above ever takes this flag off, and a colour
      // click clears the size the shopper had picked. Read from the new CTA's own wrapper,
      // which is not part of the button morph below. By value, not `toggleAttribute` - an
      // empty attribute is not the `'true'` every reader of this state tests for.
      const newAddToCartContainer = html.querySelector(
        `product-form-component[data-product-id="${this.dataset.productId}"] add-to-cart-component`,
      );
      if (newAddToCartContainer instanceof HTMLElement && newAddToCartContainer.dataset.awaitingSize === 'true') {
        currentAddToCartButtonContainer.setAttribute('data-awaiting-size', 'true');
      } else {
        currentAddToCartButtonContainer.removeAttribute('data-awaiting-size');
      }

      if (resource == null || (resource.available == false && !notify)) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      // Scoped to the form for this product, and so never to one without a product
      // id: the size guide drawer renders a `<product-form-component>` of its own,
      // deliberately without that attribute (see snippets/size-guide-content.liquid),
      // and the picker's blocks come ahead of the buy buttons — so an unscoped query
      // hands this CTA the drawer's button, which wears both of its stock labels at
      // once ("Add to cart Out of stock") and, armed by size-guide.js rather than by
      // Liquid, carries no `disabled` however the variant is stocked.
      const newAddToCartButton = html.querySelector(
        `product-form-component[data-product-id="${this.dataset.productId}"] [ref="addToCartButton"]`,
      );
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      // Last word on the awaiting-size CTA, after both the `disable` above and the morph
      // have had theirs: a colour click clears the chosen size, so the response can hand
      // back a dead "Select a size" button - which on mobile is the one pinned to the
      // bottom of the screen, and has to stay clickable there.
      currentAddToCartButtonContainer.syncAwaitingSizeState();

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100',
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step,
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;

        // Drain any queued add-to-cart requests that accumulated during the variant change
        if (this.#addToCartQueue.length > 0) {
          const queuedItems = [...this.#addToCartQueue];
          this.#addToCartQueue = [];
          this.#processBatchAddToCart(queuedItems);
        }
      }
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
